import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { animateScroll as scroll } from 'react-scroll'
import { ConfigDisplaySetting, MenuCategory } from '@open-tender/types'
import { selectDisplaySettings, useAppSelector } from '@open-tender/cloud'
import { useMenuFilters } from 'utils/helpers'
import {
  getActiveSectionId,
  hasReachedMenuEnd,
  hasSubcategories,
  makeNestedGroup,
  makeNestedGroups,
  NestedGroup,
  NestedSection,
  SectionPosition,
} from 'utils/nestedMenu'

// The shared types package does not include this setting yet.
type NestedDisplaySetting = ConfigDisplaySetting & {
  showNestedCategoryNavigation?: boolean
}

const useShowNestedCategoryNavigation = () => {
  const settings = useAppSelector(selectDisplaySettings) as
    | NestedDisplaySetting
    | undefined
  return !!settings?.showNestedCategoryNavigation
}

// Nested mode is chosen by menu structure, not by what is currently visible.
export const useNestedMenu = (categories: MenuCategory[]) => {
  const enabled = useShowNestedCategoryNavigation()
  const { filterItems } = useMenuFilters()
  const isNested = enabled && hasSubcategories(categories || [])
  const groups = isNested ? makeNestedGroups(categories, filterItems) : []
  return { groups, isNested }
}

export const useNestedCategory = (
  category: MenuCategory | null | undefined
): NestedGroup | null => {
  const enabled = useShowNestedCategoryNavigation()
  const { filterItems } = useMenuFilters()
  return enabled && category && category.children.length > 0
    ? makeNestedGroup(category, filterItems)
    : null
}

// Heights of the fixed header and of the layer under it (the parent navigation
// or the category switch), measured from the rendered elements.
export const useStickyLayers = (layerRef: RefObject<HTMLElement>) => {
  const [layers, setLayers] = useState({ header: 0, layer: 0 })

  useLayoutEffect(() => {
    const nav = document.querySelector('[aria-label="Primary Navigation"]')
    const header = nav ? nav.firstElementChild : null
    const layer = layerRef.current
    const measure = () => {
      const next = {
        header: header ? header.getBoundingClientRect().height : 0,
        layer: layer ? layer.getBoundingClientRect().height : 0,
      }
      setLayers((current) =>
        current.header === next.header && current.layer === next.layer
          ? current
          : next
      )
    }
    measure()
    window.addEventListener('resize', measure)
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (observer && header) observer.observe(header)
    if (observer && layer) observer.observe(layer)
    return () => {
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [layerRef])

  return layers
}

const SCROLL_DURATION = 400
const IDLE_MS = 150
const MAX_LOCK_MS = 1500

interface UseSectionSpyArgs {
  sections: NestedSection[]
  // Changes when the sections or their measured offsets change.
  signature: string
  // Wraps every section; its end ends the menu.
  containerRef: RefObject<HTMLElement>
  // `atStart` asks for the offset of the section's start element instead.
  getOffset: (section: NestedSection, atStart?: boolean) => number
}

// Positions are cached and re-measured only when the layout can change.
// Scrolling started by a click locks tracking until it settles.
export const useSectionSpy = ({
  sections,
  signature,
  containerRef,
  getOffset,
}: UseSectionSpyArgs) => {
  const [activeId, setActiveId] = useState<string | null>(
    sections.length > 0 ? sections[0].id : null
  )
  const sectionsRef = useRef(sections)
  const getOffsetRef = useRef(getOffset)
  const positionsRef = useRef<SectionPosition[]>([])
  const frameRef = useRef<number | null>(null)
  const lockedRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  sectionsRef.current = sections
  getOffsetRef.current = getOffset

  const measure = useCallback(() => {
    positionsRef.current = sectionsRef.current.reduce<SectionPosition[]>(
      (positions, section) => {
        const atStart = !!section.startId
        const element = document.getElementById(section.startId ?? section.id)
        if (element) {
          positions.push({
            id: section.id,
            top: element.getBoundingClientRect().top + window.scrollY,
            offset: getOffsetRef.current(section, atStart),
          })
        }
        return positions
      },
      []
    )
  }, [])

  const update = useCallback(() => {
    if (lockedRef.current) return
    const container = containerRef.current
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight
    const reachedEnd = container
      ? hasReachedMenuEnd({
          scrollY: window.scrollY,
          containerBottom: container.getBoundingClientRect().bottom,
          viewportHeight,
        })
      : false
    const next = getActiveSectionId(
      positionsRef.current,
      window.scrollY,
      reachedEnd
    )
    setActiveId((current) => (next && next !== current ? next : current))
  }, [containerRef])

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current)
    idleTimerRef.current = null
    maxTimerRef.current = null
  }, [])

  const release = useCallback(() => {
    lockedRef.current = false
    clearTimers()
    measure()
    update()
  }, [clearTimers, measure, update])

  const armIdleTimer = useCallback(
    (ms: number) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(release, ms)
    },
    [release]
  )

  useEffect(() => {
    const schedule = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        update()
      })
    }
    const onScroll = () => {
      if (lockedRef.current) armIdleTimer(IDLE_MS)
      else schedule()
    }
    const onResize = () => {
      measure()
      schedule()
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onResize)
        : null
    if (observer && containerRef.current) observer.observe(containerRef.current)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      if (observer) observer.disconnect()
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
      clearTimers()
    }
  }, [armIdleTimer, clearTimers, containerRef, measure, update])

  useEffect(() => {
    measure()
    update()
  }, [signature, measure, update])

  const scrollToSection = useCallback(
    (id: string) => {
      const section = sectionsRef.current.find((i) => i.id === id)
      const element = document.getElementById(id)
      if (!section || !element) return
      const top =
        element.getBoundingClientRect().top +
        window.scrollY -
        getOffsetRef.current(section)
      lockedRef.current = true
      setActiveId(id)
      armIdleTimer(SCROLL_DURATION + IDLE_MS)
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current)
      maxTimerRef.current = setTimeout(release, MAX_LOCK_MS)
      scroll.scrollTo(Math.max(0, top), {
        duration: SCROLL_DURATION,
        smooth: true,
      })
    },
    [armIdleTimer, release]
  )

  const resolvedId = sections.some((section) => section.id === activeId)
    ? activeId
    : sections.length > 0
    ? sections[0].id
    : null
  const activeGroupId =
    sections.find((section) => section.id === resolvedId)?.groupId ?? null

  return { activeId: resolvedId, activeGroupId, scrollToSection }
}
